import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    loading: "Vérification du paiement…",
    paymentSuccess: "Paiement confirmé",
    paymentPending: "Paiement en cours…",
    pendingMsg: "Ton paiement est en cours de validation. Cette page se rafraîchira automatiquement.",
    orderRef: "Référence",
    eventTitle: "Livestream",
    accessLink: "Ton lien d'accès personnel",
    accessLinkMsg: "Garde ce lien — c'est ta clé d'accès au portail viewer. Il est aussi envoyé à ton courriel.",
    openViewer: "🎬 Ouvrir le portail viewer",
    customerInfo: "Acheteur",
    total: "Total payé",
    backHome: "← Accueil",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
    productLabel: "Accès",
  },
  en: {
    loading: "Verifying payment…",
    paymentSuccess: "Payment confirmed",
    paymentPending: "Payment processing…",
    pendingMsg: "Your payment is being validated. This page will refresh automatically.",
    orderRef: "Reference",
    eventTitle: "Livestream",
    accessLink: "Your personal access link",
    accessLinkMsg: "Keep this link — it's your viewer portal key. Also sent to your email.",
    openViewer: "🎬 Open viewer portal",
    customerInfo: "Buyer",
    total: "Total paid",
    backHome: "← Home",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
    productLabel: "Access",
  },
};

export default function LivestreamOrderConfirmation() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") === "en" ? "en" : "fr";
  const t = T[lang];
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/livestream/public/order/${sessionId}`);
        setData(r.data);
        if (r.data?.order?.payment_status !== "paid" && tries < 10) {
          timer = setTimeout(() => setTries((n) => n + 1), 2500);
        }
      } catch {
        setData({ error: true });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, tries]);

  if (!data) return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center" data-testid="livestream-confirm-loading">{t.loading}</div>;
  if (data.error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl font-bold">❌ Erreur</div>
        <Link to="/" className="text-amber-400 hover:underline">{t.backHome}</Link>
      </div>
    );
  }

  const paid = data.order?.payment_status === "paid";
  const order = data.order;
  const event = data.event;
  const ar = data.access_right;
  const viewerUrl = ar ? `${window.location.origin}/livestream/portail/${ar.access_token}?lang=${lang}` : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="livestream-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className={`border rounded-2xl p-8 text-center ${paid ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
          <div className="text-5xl mb-4">{paid ? "✅" : "⏳"}</div>
          <div className="text-[10px] tracking-[0.4em] text-zinc-400 mb-2">{lang === "fr" ? "LIVESTREAM KAIZEN" : "KAIZEN LIVESTREAM"}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="confirm-status">{paid ? t.paymentSuccess : t.paymentPending}</h1>
          {!paid && <div className="mt-3 text-sm text-amber-200">{t.pendingMsg}</div>}
        </div>

        {order && (
          <div className="mt-8 space-y-4">
            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">{t.eventTitle.toUpperCase()}</div>
              <div className="text-xl font-bold">{event && (lang === "fr" ? event.title_fr : event.title_en)}</div>
              {event?.start_at && <div className="text-sm text-zinc-400 mt-1">📅 {new Date(event.start_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</div>}
              <div className="mt-3 text-xs">
                <span className="text-zinc-500">{t.productLabel}: </span>
                <span className="text-zinc-200">{lang === "fr" ? order.product_name_fr : order.product_name_en}</span>
                {order.replay_included && <span className="text-emerald-400 ml-2">+ Replay {order.replay_duration_hours}h</span>}
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-amber-500/20 pt-3">
                <span className="text-xs text-zinc-400">{t.total}</span>
                <span className="text-xl font-bold text-amber-300">{order.total_cad.toFixed(2)} $ CAD</span>
              </div>
            </div>

            {/* Access link — only if paid */}
            {paid && ar && viewerUrl && (
              <div className="border border-emerald-500/40 rounded-2xl p-6 bg-emerald-500/5" data-testid="access-link-block">
                <div className="text-[10px] tracking-[0.3em] text-emerald-400 mb-2">🔑 {t.accessLink.toUpperCase()}</div>
                <div className="text-xs text-zinc-300 mb-4">{t.accessLinkMsg}</div>
                <Link
                  to={`/livestream/portail/${ar.access_token}?lang=${lang}`}
                  className="block w-full text-center bg-red-500 hover:bg-red-400 text-white font-bold py-4 rounded-xl"
                  data-testid="open-viewer-btn"
                >
                  {t.openViewer}
                </Link>
                <div className="mt-3 text-[10px] text-zinc-500 font-mono break-all">{viewerUrl}</div>
              </div>
            )}

            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">{t.customerInfo.toUpperCase()}</div>
              <div className="text-sm">{order.customer_name}</div>
              <div className="text-xs text-zinc-400 mt-1">{order.customer_email}</div>
              <div className="text-[10px] text-zinc-600 mt-3 font-mono">{t.orderRef}: {order.id}</div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-full px-4 py-2 inline-block">{t.backHome}</Link>
        </div>

        <div className="mt-12 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}
